import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { passwordApi } from "../api/services";

/**
 * PasswordSetup
 *
 * Standalone page for admin-invited users to set their password.
 * URL: /setup-password?token=<single-use-token>
 *
 * Styled to match AuthContainer / LoginUser / UserRegistration.
 */
const PasswordSetup = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  // "loading" | "valid" | "success" | "error"
  const [phase,    setPhase]    = useState("loading");
  const [email,    setEmail]    = useState("");
  const [message,  setMessage]  = useState("");
  const [password, setPassword] = useState("");
  const [confirm,  setConfirm]  = useState("");
  const [showPw,   setShowPw]   = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const passwordRef = useRef(null);

  // ── Verify token on mount ──────────────────────────────────────────────────
  useEffect(() => {
    if (!token) {
      setMessage("No setup token found in the URL. Please use the link from your invitation email.");
      setPhase("error");
      return;
    }

    passwordApi
      .verifySetupToken(token)
      .then((res) => {
        const data = res.data?.data || res.data;
        setEmail(data.email || "");
        setPhase("valid");
        setTimeout(() => passwordRef.current?.focus(), 100);
      })
      .catch((err) => {
        setMessage(
          err.response?.data?.message ||
          err.response?.data?.error ||
          "This setup link is invalid or has expired."
        );
        setPhase("error");
      });
  }, [token]);

  // ── Submit new password ────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }

    setIsLoading(true);
    try {
      await passwordApi.setupPassword(token, password);
      setPhase("success");
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Something went wrong. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // ── Password strength ──────────────────────────────────────────────────────
  const strength = (() => {
    if (!password) return null;
    let s = 0;
    if (password.length >= 8)           s++;
    if (password.length >= 12)          s++;
    if (/[A-Z]/.test(password))         s++;
    if (/[0-9]/.test(password))         s++;
    if (/[^A-Za-z0-9]/.test(password))  s++;
    if (s <= 1) return { label: "Weak",   color: "bg-red-400",    w: "w-1/4" };
    if (s <= 2) return { label: "Fair",   color: "bg-orange-400", w: "w-2/4" };
    if (s <= 3) return { label: "Good",   color: "bg-yellow-400", w: "w-3/4" };
    return       { label: "Strong", color: "bg-green-500",  w: "w-full" };
  })();

  const inputCls =
    "w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm " +
    "focus:outline-none focus:border-blue-900 focus:ring-1 focus:ring-blue-900 transition-colors";

  // ── Render states ──────────────────────────────────────────────────────────
  const renderContent = () => {

    if (phase === "loading") {
      return (
        <div className="flex flex-col items-center gap-4 py-6">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-900" />
          <p className="text-sm text-gray-500">Validating your invitation link…</p>
        </div>
      );
    }

    if (phase === "error") {
      return (
        <div className="text-center py-4">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-gray-800 mb-2">Link Invalid or Expired</h2>
          <p className="text-sm text-gray-500 mb-6">{message}</p>
          <p className="text-xs text-gray-400 mb-6">
            Contact your administrator to resend the invitation.
          </p>
          <Link to="/" className="text-sm text-blue-900 font-semibold hover:underline">
            ← Back to Login
          </Link>
        </div>
      );
    }

    if (phase === "success") {
      return (
        <div className="text-center py-4">
          <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Password Set!</h2>
          <p className="text-sm text-gray-500 mb-8">
            Your account is ready. You can now log in with your email and new password.
          </p>
          <Link
            to="/"
            className="w-full block py-3 bg-blue-900 text-white font-bold rounded-lg shadow-md hover:bg-blue-800 transition-colors text-center"
          >
            Go to Login
          </Link>
        </div>
      );
    }

    // phase === "valid" — show the set-password form
    return (
      <>
        <p className="text-sm text-gray-500 text-center mb-6">
          Setting up account for <span className="font-semibold text-blue-900">{email}</span>
        </p>

        <form onSubmit={handleSubmit} className="w-full max-w-xs space-y-4">

          {/* New Password */}
          <div>
            <div className="relative">
              <input
                ref={passwordRef}
                type={showPw ? "text" : "password"}
                placeholder="New password (min 8 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className={inputCls}
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-blue-900 transition-colors"
              >
                {showPw ? "Hide" : "Show"}
              </button>
            </div>
            {/* Strength bar */}
            {strength && (
              <div className="mt-2">
                <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-300 ${strength.color} ${strength.w}`} />
                </div>
                <p className={`text-xs mt-1 ${strength.color.replace("bg-", "text-")}`}>
                  {strength.label} password
                </p>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <input
              type={showPw ? "text" : "password"}
              placeholder="Confirm new password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              className={`${inputCls} ${confirm && confirm !== password ? "border-red-400 focus:border-red-500 focus:ring-red-300" : ""}`}
            />
            {confirm && confirm !== password && (
              <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading || !password || !confirm}
            className="w-full py-3 bg-blue-900 text-white font-bold rounded-lg shadow-md hover:bg-blue-800 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isLoading ? "Setting password…" : "Set Password & Activate Account"}
          </button>
        </form>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 font-sans">
      <Toaster position="top-right" />

      {/* White card — matches AuthContainer single-panel style */}
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-10 flex flex-col items-center">

        {/* Logo / Brand */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-blue-900 tracking-wide">QEDGE</h1>
          <p className="text-xs text-gray-400 mt-1">Industrial IoT Platform</p>
        </div>

        {/* Page title */}
        <h2 className="text-2xl font-bold text-gray-800 text-center mb-1">
          {phase === "valid" ? "Set Your Password" : "Account Setup"}
        </h2>
        {phase === "valid" && (
          <p className="text-sm text-gray-500 text-center mb-2">
            Choose a secure password to activate your account.
          </p>
        )}

        <div className="w-full mt-4">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default PasswordSetup;